import { FC, useEffect, useState } from "react";
import { useSingleUserControl } from "../../../context/SuperAdmin/SingleUserControlContext";
import { Box, Button, Modal, Typography } from "@mui/material";
import { iDataUserRoles } from "../../../interfaces/SuperAdmin/Pages/iDataUserRoles";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const DetachModal: FC<Props> = ({ isOpen, setIsOpen }) => {
  const { suscriptions, myRoles, mySuborgs, isDetaching, detachUser } =
    useSingleUserControl();

  const [canDetach, setCanDetach] = useState(false);
  const [notAllowedRoles, setNotAllowedRoles] = useState<iDataUserRoles[]>([]);

  const handleClose = () => {
    if (!isDetaching) {
      setIsOpen(false);
    }
  };

  // Excluimos el Admin de Dominio, ya que este no tiene por qué eliminarse
  useEffect(() => {
    const filteredRoles = myRoles.filter((rol) => rol.id_organizacion);
    setNotAllowedRoles(filteredRoles);
  }, [myRoles]);

  // Definimos si se puede o no desanexar a la organización
  useEffect(() => {
    // Variables que indican si tenemos suscripciones, suborganizaciones o roles activos
    const activeSus = Boolean(suscriptions.length);
    const activeSuborgs = Boolean(mySuborgs.length);
    const activeRoles = Boolean(notAllowedRoles.length);

    // Si alguno está activo, no se puede
    if (activeSus || activeSuborgs || activeRoles) {
      setCanDetach(false);
    } else {
      setCanDetach(true);
    }
  }, [suscriptions, notAllowedRoles, mySuborgs]);

  // Cerramos el popup cuando se ejecute la función
  useEffect(() => {
    if (!isDetaching && isOpen) {
      handleClose();
    }
  }, [isDetaching]);

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(0, 0, 0, 0.3)", // Fondo negro con opacidad
      }}
    >
      <Box
        component={"div"}
        sx={{
          width: 400,
          bgcolor: "background.paper", // Fondo blanco del modal
          borderRadius: 2, // Bordes redondeados
          p: 4,
          boxShadow: 24,
          minWidth: "600px",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Typography component={"h3"} sx={{ fontSize: "20px", fontWeight: 600 }}>
          {canDetach
            ? "¿Quieres desanexar este usuario de su organización?"
            : "No puedes desanexar este usuario aún"}
        </Typography>

        {!canDetach ? (
          <>
            {/* Lista de entidades a eliminar */}
            <Box>
              <Typography sx={{ mb: 1 }}>
                Primero debes eliminar al usuario de las siguientes entidades
                relacionadas a la organización:
              </Typography>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: "20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "14px",
                }}
              >
                {/* Lista de suscripciones */}
                {suscriptions.length ? (
                  <li>
                    <Typography
                      component="span"
                      sx={{ color: "red", fontWeight: "bold" }}
                    >
                      Suscripciones ({suscriptions.length}):
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: "20px" }}>
                      {suscriptions.map((row) => (
                        <li>{row.plan}</li>
                      ))}
                    </ul>
                  </li>
                ) : (
                  <></>
                )}

                {/* Lista de roles */}
                {notAllowedRoles.length ? (
                  <li>
                    <Typography
                      component="span"
                      sx={{ color: "red", fontWeight: "bold" }}
                    >
                      Roles ({notAllowedRoles.length}):
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: "20px" }}>
                      {notAllowedRoles.map((row) => (
                        <li>{row.rol}</li>
                      ))}
                    </ul>
                  </li>
                ) : (
                  <></>
                )}

                {/* Lista de suborganizaciones */}
                {mySuborgs.length ? (
                  <li>
                    <Typography
                      component="span"
                      sx={{ color: "red", fontWeight: "bold" }}
                    >
                      Suborganizaciones ({mySuborgs.length}):
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: "20px" }}>
                      {mySuborgs.map((row) => (
                        <li>{row.suborganizacion}</li>
                      ))}
                    </ul>
                  </li>
                ) : (
                  <></>
                )}
              </ul>
            </Box>{" "}
          </>
        ) : (
          <>
            <Typography sx={{ mb: 1 }}>
              El usuario NO podrá acceder a la app mientras no tenga una
              organización
            </Typography>
            <Box
              sx={{
                textAlign: "right",
                display: "flex",
                justifyContent: "flex-end",
                gap: 3,
                mt: 3,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => setIsOpen(false)}
                disabled={isDetaching}
                sx={{
                  borderRadius: 14,
                  border: "1px solid #336B87",
                  color: "#336B87",
                  "&:hover": {
                    backgroundColor: "#336B87",
                    color: "#EEF3F6",
                  },
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                sx={{ background: "#336B87" }}
                disabled={isDetaching}
                onClick={detachUser}
              >
                Confirmar
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default DetachModal;
